<template>
  <div class="rounded shadow-sm bg-white px-3 py-2 md:px-8 md:py-5 max-w-screen-lg mx-auto">
    <template v-if="apptemplate">
      <div class="flex flex-col py-3">
        <label class="block"> Form Name </label>
        <SingleLineInput
          :value="apptemplate.name"
          @input="apptemplate.name = $event.target.value"
        ></SingleLineInput>
        <p class="text-brand-mid-gray text-xs py-1 italic">
          Form name is only visible to you. This helps you to organize your application forms.
        </p>
      </div>

      <div class="flex flex-col py-3">
        <label class="block">Application Form Link </label>
        <div class="flex flex-row items-center py-2">
          <div class="border border-brand-mid-gray rounded-tl rounded-bl py-2 pl-5 pr-3 h-full">
            <p class="">{{ origin }}/</p>
          </div>
          <div class="w-full">
            <input
              type="text"
              class="w-full px-4 text-sm md:text-base rounded-tr rounded-br border border-brand-mid-gray border-l-0 placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
              placeholder="slug"
              :class="{ 'bg-brand-light-gray': !!apptemplate.slug }"
              :value="apptemplate.slug"
              @change="validateSlug"
            />
          </div>
        </div>
        <p class="text-brand-red text-sm" v-if="slugErrorMessage">
          {{ slugErrorMessage }}
        </p>
        <AlertToast
          v-if="apptemplate.slug !== slug && apptemplate.has_active_invitations"
          class="my-1"
        >
          <div class="flex py-3 px-5">
            <div class="flex items-center w-full">
              <p>
                It looks like you've recently sent this link to potential applicants. Changing the
                link now will mean the previous link will not be usable, and you may want to send it
                again.
              </p>
            </div>
          </div>
        </AlertToast>

        <p class="text-brand-mid-gray text-xs py-1 italic">
          This URL will link your applicants directly to your application. Your link can contain a
          combination of lowercase letters, numbers, underscores and hyphens (no spaces) and must be
          at least 6 characters long.
        </p>

        <p class="text-brand-mid-gray text-xs py-1 italic">
          (examples: abc _realty_101centralst, john-smith-property1)
        </p>
      </div>

      <div class="flex flex-col py-3">
        <h5 class="pb-3">Reports to Order With Application</h5>

        <div class="flex flex-col md:px-32">
          <div
            class="flex flex-row py-2"
            v-for="rsp in apptemplate.available_report_sources"
            :key="rsp.id"
          >
            <div class="pr-5">
              <input
                :id="`rsp-checkbox-option-${rsp.id}`"
                type="checkbox"
                class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                v-model="selected[rsp.source_identifier]"
                :disabled="!rsp.active"
              />
            </div>

            <div class="flex flex-col w-full">
              <div class="flex flex-row justify-between">
                <div class="flex items-center">
                  <label :for="`rsp-checkbox-option-${rsp.id}`">
                    {{ rsp.select_report_label }}
                  </label>
                </div>
                <div class="flex items-center relative">
                  <div
                    @click.self="toggleTooltip(`rsp-${rsp.id}`)"
                    @mouseenter="toggleTooltip(`rsp-${rsp.id}`)"
                    @mouseleave="toggleTooltip(`rsp-${rsp.id}`)"
                  >
                    <QuestionMarkCircleIcon
                      class="w-5 h-5 md:w-7 md:h-7 mr-2 text-brand-mid-gray"
                      :id="`rsp-${rsp.id}`"
                    />
                  </div>

                  <div
                    :class="[
                      'w-64 py-2 px-2 mt-2 bg-gray-600 absolute right-24 px-1 rounded bg-opacity-50 shadow-xl',
                      tooltip[`rsp-${rsp.id}`] ? 'inline-block' : 'hidden'
                    ]"
                  >
                    <p class="text-sm whitespace-pre-wrap text-gray-100">{{ rsp.tooltip_label }}</p>
                  </div>
                  <h5 class="w-12 md:w-16 text-right">${{ rsp.price }}</h5>
                </div>
              </div>
              <p
                v-if="rsp.source_identifier === 'experian_credit'"
                class="text-brand-dark-gray text-xs py-1 italic"
              >
                {{
                  rsp.active ? rsp.process_label : "To select the credit report option you need to "
                }}
                <router-link
                  v-if="!rsp.active"
                  :to="{ name: 'dashboard_verify_disclaimer' }"
                  class="text-brand-green hover:underline font-semibold not-italic"
                  >Verify your Identify through Experian</router-link
                >
              </p>

              <p
                v-if="rsp.source_identifier !== 'experian_credit'"
                class="text-brand-dark-gray text-xs py-1 italic"
              >
                {{ rsp.process_label }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col py-3" v-if="apptemplate.landlord.payments_enabled">
        <div class="flex flex-row justify-between">
          <div class="flex items-center">
            <h5>
              Additional Application Fee
              <span class="text-xs text-brand-mid-gray md:ml-1">(optional)</span>
            </h5>
          </div>
          <div class="flex flex-row items-center justify-end md:pr-32">
            <div class="border border-brand-mid-gray rounded-tl rounded-bl py-2 pl-5 pr-3">
              <p class="">$</p>
            </div>
            <div class="md:w-24">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                class="w-full px-4 text-sm md:text-base rounded-tr rounded-br border border-brand-mid-gray border-l-0 placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
                placeholder="0.00"
                :class="{ 'bg-brand-light-gray': !!apptemplate.fee }"
                :value="apptemplate.fee"
                @change="validateFee"
              />
            </div>
          </div>
        </div>
        <div class="flex flex-col md:w-1/2 pt-2 md:pt-0">
          <p class="text-xs text-brand-mid-gray">
            Add on an extra fee from $1 up to $100 on top of the reports base fee.
          </p>
          <p class="text-xs text-brand-mid-gray">
            Fees will be accrued in your My payments account.
          </p>
          <p class="text-xs text-brand-mid-gray">
            For fees over $100, please contact support@rentapplication.net.
          </p>
        </div>
      </div>

      <div class="flex flex-col py-3">
        <div class="flex flex-row justify-between">
          <div class="flex flex-col justify-center">
            <h5 class="text-lg font-semibold">Total Fee Paid by Applicant</h5>
            <template v-if="hasReportsSelected && getCurrentUser.landlord.technology_fee > 0">
              <p class="text-xs text-brand-dark-gray italic">
                * Includes additional technology fee of ${{
                  getCurrentUser.landlord.technology_fee
                }}
              </p>
            </template>
          </div>
          <div class="flex items-center md:pr-32">
            <h5 class="text-lg font-semibold">${{ totalFee }}</h5>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col py-3"
        v-if="!hasReportsSelected && !getCurrentUser.landlord.verified"
      >
        <AlertToast>
          <div class="py-3 px-3">
            <p>
              Warning: Not requesting any reports will show applicants a warning when opening an
              application. Click
              <router-link :to="{ name: 'home_applicant_warning_message' }" class="text-brand-green"
                >here</router-link
              >
              to learn why we do this
            </p>
          </div>
        </AlertToast>
      </div>

      <div class="flex flex-col py-5">
        <div class="w-full md:w-1/3 mx-auto">
          <PrimaryButton
            @click="updateFormSettings"
            :disabled="isApplicationTemplateLoading"
            :is-loading="isApplicationTemplateLoading"
            >Save
          </PrimaryButton>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { mapGetters } from "vuex"
import _ from "lodash"

import { QuestionMarkCircleIcon } from "@heroicons/vue/outline"

import { APPLICATION_TEMPLATE_UPDATE_SETTINGS } from "@/store/types"

export default {
  components: {
    QuestionMarkCircleIcon
  },
  computed: {
    ...mapGetters([
      "getCurrentApplicationTemplate",
      "isApplicationTemplateLoading",
      "getCurrentUser"
    ]),
    origin() {
      return location.origin
    },
    hasReportsSelected() {
      return _.some(Object.values(this.selected))
    },
    totalFee() {
      return (
        parseFloat(this.apptemplate.fee || 0) +
        (this.hasReportsSelected ? parseFloat(this.getCurrentUser.landlord.technology_fee) : 0) +
        Object.keys(this.selected)
          .map((key) => {
            if (this.selected[key]) {
              return parseFloat(
                this.apptemplate.available_report_sources.find(
                  (rsp) => rsp.source_identifier === key
                ).price
              )
            }
            return 0
          })
          .reduce((total, v) => total + v, 0)
      ).toFixed(2)
    }
  },
  data() {
    return {
      slug: "",
      apptemplate: null,
      selected: {},
      tooltip: {},
      slugErrorMessage: "",
      infoPageSlugs: [
        "brokerage",
        "security",
        "applicant-warning-message",
        "applicant-ssn",
        "how-to-get-verified-for-credit-reports",
        "privacy",
        "termsofuse",
        "support",
        "applicants",
        "report-accuracy",
        "applicant-benefits",
        "free-rental-application-pdf",
        "tenant-background-check",
        "tenant-screening",
        "tenant-credit-check",
        "online-rental-application",
        "online-apartment-application",
        "landlord-forms",
        "landlord-tenant-forms",
        "forms-for-landlords",
        "condo.com",
        "fill-your-vacancies"
      ]
    }
  },
  watch: {
    getCurrentApplicationTemplate: {
      handler(val) {
        if (val) {
          this.apptemplate = _.cloneDeep(val)
          const selected = {}
          this.apptemplate.selected_report_sources.map((rsp) => {
            selected[rsp.source_identifier] = true
          })
          this.selected = selected

          this.apptemplate.available_report_sources = _.orderBy(
            this.apptemplate.available_report_sources.map((rsp) => {
              if (rsp.source_identifier.includes("credit")) {
                rsp.process_label =
                  "Typically processed within 5 minutes. Soft inquiry on credit file."
                rsp.order = 1

                if (rsp.source_identifier.includes("experian")) {
                  rsp.select_report_label = "Credit Report by Experian"
                  rsp.tooltip_label =
                    "Comprehensive credit reporting including Vantage score, detailed trade line information, public records, and more provided by Experian"
                } else if (rsp.source_identifier.includes("zip_report")) {
                  rsp.select_report_label = "Credit Report by Transunion"
                  rsp.tooltip_label =
                    "Comprehensive credit reporting including Vantage score, detailed trade line information, public records, and more provided by Transunion"
                } else {
                  rsp.select_report_label = "Credit Report by Transunion"
                  rsp.tooltip_label =
                    "Comprehensive credit reporting including FICO score, detailed trade line information, public records, and more provided by Transunion"
                }
              }

              if (rsp.source_identifier.includes("identity")) {
                rsp.select_report_label = "Identity Verification"
                rsp.order = 0
                rsp.process_label =
                  "Typically processed within few minutes after applicant completes the verification"
                rsp.tooltip_label =
                  "Automated identity verification that looks for patterns to determine if an ID document is real or fake. Also captures real-time photos of the applicant to match them with the ID document."
              }

              if (rsp.source_identifier.includes("criminal")) {
                rsp.select_report_label = "Criminal Record Search"
                rsp.order = 3
                rsp.process_label = "Typically processed within 5 minutes"
                rsp.tooltip_label =
                  "Criminal background search of over 500 MILLION records from all 50 states and includes a bonus FREE search of sex and violent offender criminal records and terrorist check"
              }

              if (rsp.source_identifier.includes("eviction")) {
                rsp.select_report_label = "Evictions, Judgements, and Liens"
                rsp.order = 4
                rsp.process_label = "Typically processed within 5 minutes"
                rsp.tooltip_label =
                  "Database of millions of public records for eviction filings, liens, civil suits, and judgements. This report may disclose additional information about a prospective tenant that would not always show up on a credit report or criminal background check. Supports all 50 states, but county coverage may vary."
              }

              if (rsp.source_identifier.includes("employment")) {
                rsp.select_report_label = "Employment Verification"
                rsp.order = 2
                rsp.process_label = "Typically processed within 3 business days"
                rsp.tooltip_label =
                  "We contact the applicant's stated employer to confirm their status and other details about their employment. Because this report requires the current employer's participation, reports may be delayed up to 3 business days. If after 3 business days the employer does not respond to our inquiry, the inquiry will be closed."

                if (rsp.source_identifier.includes("plus")) {
                  rsp.process_label =
                    "Income Verification processed immediately. Employment verification processed within 3 business days."
                  rsp.select_report_label = "Income + Employment Verification"
                  rsp.tooltip_label =
                    "Applicant can also verify their income by connecting their bank account. The previous 12 months of deposit history is analyzed to determine their estimated yearly income. This is ready immediately on application submission.\n\nFor the Employment Verification, we contact the applicant's stated employer to confirm their status and other details about their employment. Because this report requires the current employer's participation, this part of the report may be delayed up to 3 business days. If after 3 business days the employer does not respond to our inquiry, the inquiry will be closed."
                }
              }
              if (rsp.source_identifier.includes("landlord")) {
                rsp.process_label = "Typically processed within 3 business days"
                const type = rsp.source_identifier.includes("previous") ? "previous" : "current"
                rsp.tooltip_label = `We confirm the applicant's residence and reputation with the primary contact at their ${type} address. Because this report requires the ${type} landlords participation, reports may be delayed up to 3 business days. If after 3 business days the ${type} landlord does not respond to our inquiry, the inquiry will be closed.`

                if (
                  rsp.source_identifier.includes("current") ||
                  rsp.source_identifier === "mvga_landlord"
                ) {
                  rsp.select_report_label = "Current Residence Verification"
                  rsp.order = 5
                } else rsp.select_report_label = "Previous Residence Verification"
                rsp.order = 6
              }
              return rsp
            }),
            ["order"]
          )
        }
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    /* TODO: Set infoPageSlugs dynamically once router is fully built out */
    //this.infoPageSlugs = this.$router.getRoutes().map(route => route.path.split("/")[1])
    this.slug = this.$route.params.slug
  },
  methods: {
    toggleTooltip(rspId) {
      this.tooltip[rspId] = !this.tooltip[rspId]
    },
    validateFee(ev) {
      const newFee = parseFloat(ev.target.value)
      if (newFee > 100) {
        this.apptemplate.fee = 0
      } else {
        this.apptemplate.fee = ev.target.value
      }
    },
    validateSlug(ev) {
      const newSlug = ev.target.value.toLowerCase()
      if (newSlug.length < 6) {
        this.slugErrorMessage = "URL link must be at least 6 characters long"
        return
      }

      if (/^\d+$/.test(newSlug)) {
        this.slugErrorMessage = "URL link must contain at least one letter"
        return
      }

      if (this.infoPageSlugs.includes(newSlug)) {
        this.slugErrorMessage = "URL link is already taken, please try another"
        return
      }

      this.slugErrorMessage = ""
      this.apptemplate.slug = newSlug
    },
    async updateFormSettings() {
      const { name, fee } = this.apptemplate

      const selectedReports = Object.keys(this.selected)
        .map((key) => {
          return this.selected[key] ? key : ""
        })
        .filter(Boolean)

      try {
        await this.$store.dispatch(APPLICATION_TEMPLATE_UPDATE_SETTINGS, {
          slug: this.$route.params.slug,
          newSlug: this.apptemplate.slug,
          name,
          fee,
          selectedReports
        })
        // Route to forms and reload apptemplates
        this.$router.push({ name: "dashboard_forms", query: { reload: true } })
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>
